import React, { useState } from 'react';
import { useTranslation } from 'react-i18next'; // Add this import
import Busimg from '../../components/assets/bus.jpg';
import logo from '../../components/assets/logos/logo2.PNG';
import { useNavigate } from 'react-router-dom';

// Define the responsive styles object with yellow theme
const responsiveStyles = {
  landingPage: {
    display: 'flex',
    flexDirection: 'row',
    background: '#FFFFFF',
    overflow: 'hidden',
    height: '100vh',
  },

  frame1: {
    width: '45%',
    minWidth: '350px',
    maxWidth: '731px',
    background: 'linear-gradient(135deg, #FFF9C4 0%, #FFFDE7 100%)', // Light yellow gradient
    boxShadow: '4px 0px 8px 3px rgba(0, 0, 0, 0.1)', // Softer shadow
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '5vh 2vw',
    position: 'relative',
    gap: '25px', // Slightly increased gap
  },

  logoContainer: {
    width: 'min(50%, 250px)',
    marginBottom: '10px',
  },

  logoImage: {
    width: '100%',
    height: 'auto',
    display: 'block',
    filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.1))', // Subtle shadow on logo
  },

  welcomeText: {
   fontFamily: "'Noto Serif Devanagari', serif",

    fontStyle: 'normal',
    fontWeight: '700',
    fontSize: 'min(4vw, 30px)',
    lineHeight: '1.3',
    textAlign: 'center',
    color: '#5D4037', // Dark brown for better contrast with yellow
    padding: '0 5%',
    flexGrow: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '80%',
    textShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
  },
  
  // 🟡 YELLOW THEME LOGIN BUTTON
  loginButtonContainer: {
    width: 'min(70%, 301px)',
    height: '70px',
    background: 'linear-gradient(145deg, #FFEB3B 0%, #FFD54F 100%)', // Vibrant yellow gradient
    border: 'none',
    borderRadius: '75px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(255, 193, 7, 0.3), 0 0 8px rgba(255, 235, 59, 0.4)',
    marginTop: '10px',
    position: 'relative',
    overflow: 'hidden',
  },

  // 🟡 HOVER EFFECTS FOR YELLOW BUTTON
  loginButtonHover: {
    background: 'linear-gradient(145deg, #FFD54F 0%, #FFC107 100%)', // Darker yellow on hover
    boxShadow: '0 6px 20px rgba(255, 193, 7, 0.4), 0 0 12px rgba(255, 235, 59, 0.6)',
    transform: 'translateY(-2px) scale(1.02)',
  },

  // 🟡 ACTIVE/CLICK EFFECT
  loginButtonActive: {
    transform: 'translateY(0) scale(0.98)',
    boxShadow: '0 2px 8px rgba(255, 193, 7, 0.2)',
  },

  loginText: {
fontFamily: "'Noto Serif Devanagari', serif",

    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: 'min(5vw, 30px)',
    color: '#5D4037', // Dark brown text for contrast
    letterSpacing: '0.5px',
    textShadow: '0px 1px 1px rgba(255, 255, 255, 0.5)',
  },

  busImageContainer: {
    flexGrow: 1,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    background: '#FFFDE7', // Light yellow background for image container
  },
  
  busImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    opacity: '0.9',
  },

  // Optional: Add a decorative element
  decorativeCorner: {
    position: 'absolute',
    top: '0',
    right: '0',
    width: '100px',
    height: '100px',
    background: 'radial-gradient(circle at top right, #FFEB3B 0%, transparent 70%)',
    opacity: '0.2',
  }
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(); // Initialize translation hook
  const [isHovering, setIsHovering] = useState(false);
  const [isActive, setIsActive] = useState(false);
  
  const handleLoginClick = () => {
    navigate('/login');
  };

  // Combine base styles, hover styles, and active styles conditionally
  const buttonStyle = {
    ...responsiveStyles.loginButtonContainer,
    ...(isHovering ? responsiveStyles.loginButtonHover : {}),
    ...(isActive ? responsiveStyles.loginButtonActive : {})
  };

  return (
    <div style={responsiveStyles.landingPage}>
      <div style={responsiveStyles.frame1}>
        {/* Decorative corner element */}
        <div style={responsiveStyles.decorativeCorner}></div>
        
        <div style={responsiveStyles.logoContainer} role="img" aria-label={t('logoAlt', 'Rajdoot Logo')}>
          <img 
            src={logo} 
            alt={t('logoAlt', 'Rajdoot Logo')} 
            style={responsiveStyles.logoImage} 
          />
        </div>

        <div style={responsiveStyles.welcomeText}>
          {t('welcomeMessageLanding', 'WELCOME TO THE RAJDOOT TRANSPORT MANAGEMENT SYSTEM')}
        </div>
        
        <div 
          style={buttonStyle}
          onClick={handleLoginClick}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => {
            setIsHovering(false);
            setIsActive(false);
          }}
          onMouseDown={() => setIsActive(true)}
          onMouseUp={() => setIsActive(false)}
          onTouchStart={() => setIsActive(true)}
          onTouchEnd={() => setIsActive(false)}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === 'Enter' && handleLoginClick()}
        >
          <div style={responsiveStyles.loginText}>
            {t('login', 'Login')}
          </div>
        </div>
      </div>

      <div style={responsiveStyles.busImageContainer} role="img" aria-label={t('busImageAlt', 'Bus image')}>
        <img 
          src={Busimg} 
          alt={t('busImageAlt', 'Bus image')} 
          style={responsiveStyles.busImage} 
        />
      </div>
    </div>
  );
};

export default LandingPage;